import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Dialog, DialogFooter } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/submit/SubmitForm";
import { encodeForApi } from "@/lib/formEncode";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import { BRAND } from "@/lib/tokens";
import type { CellValue, ColumnDef, UUID } from "@/lib/types";

// Field types the form can render + encode (mirrors SubmitForm's renderControl).
// person uses an INLINE search (not a portaled picker → no modal focus-trap);
// file / meta columns stay out of the quick add-row form.
const SUPPORTED = new Set([
  "text",
  "long_text",
  "status",
  "date",
  "number",
  "tags",
  "link",
  "checkbox",
  "person",
]);

type StatusOption = { id: string; label: string; color?: string; archived?: boolean };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
  /** This board's columns — the modal renders a field per supported column. */
  columns: ColumnDef[];
  /** The grouping column id (if the board is grouped): its field gets a
   * "+ Neue Gruppe" inline-create so a row can start a brand-new group. */
  groupColumnId?: string | null;
  /** Inline-create a new option (group) on a status/tags column. */
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
}

export function AddRowDialog({
  open,
  onOpenChange,
  boardId,
  columns,
  groupColumnId = null,
  onAddColumnOption,
}: Props) {
  const qc = useQueryClient();
  const fields = useMemo(() => columns.filter((c) => SUPPORTED.has(c.type)), [columns]);
  const firstText = fields.find((c) => c.type === "text") ?? null;

  const [values, setValues] = useState<Record<UUID, unknown>>({});
  const [errors, setErrors] = useState<Record<UUID, string>>({});
  const [busy, setBusy] = useState(false);

  function setVal(colId: UUID, v: unknown) {
    setValues((prev) => ({ ...prev, [colId]: v }));
    setErrors((prev) => {
      if (!(colId in prev)) return prev;
      const next = { ...prev };
      delete next[colId];
      return next;
    });
  }

  function reset() {
    setValues({});
    setErrors({});
  }

  async function create(keepOpen: boolean) {
    if (busy) return;
    const errs: Record<UUID, string> = {};
    if (firstText) {
      const raw = values[firstText.id];
      if (typeof raw !== "string" || raw.trim() === "") errs[firstText.id] = t.submitRequiredField;
    }
    const payload: Record<UUID, CellValue> = {};
    for (const col of fields) {
      const raw = values[col.id];
      if (raw === undefined || raw === null || raw === "") continue;
      try {
        payload[col.id] = encodeForApi(col, raw);
      } catch (err) {
        errs[col.id] = err instanceof Error ? err.message : "Ungültiger Wert";
      }
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setBusy(true);
    try {
      const row = await api.createRow(boardId, {});
      for (const [columnDefId, value] of Object.entries(payload)) {
        await api.upsertCell(row.id as UUID, columnDefId as UUID, { value });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.board(boardId) });
      showToast(t.addRowCreated, "success");
      if (keepOpen) reset();
      else onOpenChange(false);
    } catch (err) {
      const msg =
        err instanceof ApiError && typeof err.detail === "object" && err.detail
          ? JSON.stringify(err.detail)
          : err instanceof Error
            ? err.message
            : t.unknownError;
      showToast(msg, "error", 4000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={t.addRowDialogTitle}
      description={t.addRowDialogDescription}
      width={500}
    >
      {fields.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--ink-3)", padding: "8px 2px" }}>
          {t.addRowDialogNoFields}
        </div>
      ) : (
        <div style={{ marginTop: 4 }}>
          {fields.map((col) =>
            col.id === groupColumnId && col.type === "status" && onAddColumnOption ? (
              <GroupField
                key={col.id}
                column={col}
                value={(values[col.id] as string) ?? ""}
                onChange={(v) => setVal(col.id, v)}
                onAddColumnOption={onAddColumnOption}
              />
            ) : (
              <FormField
                key={col.id}
                column={col}
                value={values[col.id]}
                onChange={(v) => setVal(col.id, v)}
                error={errors[col.id]}
                required={col.id === firstText?.id}
                boardId={boardId}
              />
            ),
          )}
        </div>
      )}
      <DialogFooter>
        <Button variant="ghost" size="md" disabled={busy} onClick={() => void create(true)}>
          {t.addRowDialogSaveAndNew}
        </Button>
        <Button
          variant="primary"
          size="md"
          icon="check"
          disabled={busy || fields.length === 0}
          onClick={() => void create(false)}
        >
          {busy ? t.submitting : t.addRowDialogCreate}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// The grouping column's field: pick an existing group OR create a new one inline
// ("+ Neue Gruppe"). The create input lives INSIDE the dialog body (not a portaled
// popover) so the Radix modal focus-trap doesn't eat keystrokes.
function GroupField({
  column,
  value,
  onChange,
  onAddColumnOption,
}: {
  column: ColumnDef;
  value: string;
  onChange: (v: string) => void;
  onAddColumnOption: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
}) {
  const opts = ((column.config.options as StatusOption[]) ?? []).filter((o) => !o.archived);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    const trimmed = label.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const opt = await onAddColumnOption(column, trimmed);
      if (opt) onChange(opt.id);
      setLabel("");
      setCreating(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 500,
          color: "var(--ink-2)",
          marginBottom: 5,
        }}
      >
        {column.name}
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        {opts.map((o) => {
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(active ? "" : o.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? BRAND.blue : "var(--hairline-strong)"}`,
                background: active ? "var(--surface-elev)" : "var(--surface)",
                color: "var(--ink-1)",
                fontWeight: active ? 600 : 500,
                fontSize: 12.5,
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                cursor: "pointer",
              }}
            >
              {o.label}
            </button>
          );
        })}
        {creating ? (
          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t.addGroupPlaceholder}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void add();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setCreating(false);
                  setLabel("");
                }
              }}
              style={{
                height: 30,
                minWidth: 0,
                width: 130,
                padding: "0 9px",
                fontSize: 12.5,
                borderRadius: 7,
                border: "1px solid var(--hairline-strong)",
                background: "var(--surface)",
                color: "var(--ink-1)",
                outline: "none",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            />
            <button
              type="button"
              onClick={() => void add()}
              disabled={busy || !label.trim()}
              style={{
                height: 30,
                padding: "0 10px",
                borderRadius: 7,
                border: "none",
                background: BRAND.blue,
                color: "#fff",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: busy ? "wait" : "pointer",
              }}
            >
              {t.addGroupConfirm}
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: "1px dashed var(--hairline-strong)",
              background: "transparent",
              color: "var(--accent)",
              fontSize: 12.5,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            }}
          >
            + {t.addGroupButton}
          </button>
        )}
      </div>
    </div>
  );
}
